import { PublicClientApplication } from '@azure/msal-browser';
import { Client } from '@microsoft/microsoft-graph-client';

const TIMEOUT_TRACKING = 15000;            // 15s
const TIMEOUT_REPORT = 60000;              // 60s (forwarding + processing)
const TIMEOUT_DELETE = 30000;              // 30s
const TIMEOUT_TESTING = 300000;            // 5 minutes

// Test constants until lookup is available
const CIMENTO_PRODUCTION_APP = "cimento.app";
const CIMENTO_STAGING_APP = "staging.cimento.app";
const CIMENTO_PRODUCTION_API = "api.cimento.app";
const CIMENTO_STAGING_API = "api-staging.cimento.app";
const CIMENTO_LOCAL_API = "localhost:8080";

const CIMENTO_APP = CIMENTO_PRODUCTION_APP;
const CIMENTO_API = CIMENTO_PRODUCTION_API;

const REPORT_URL = 'https://' + CIMENTO_API + '/api/pab/outlook/messages/report';
const DELETE_URL = 'https://' + CIMENTO_API + '/api/pab/outlook/messages/';

let account = null;
let configValues = null;
let debug = false;

Office.onReady(() => {

  configValues = __AAD_CONFIG__;

  if (window.location.host.indexOf('localhost:3000') >= 0) {
    debug = true;
  }

});

/**
 * Check if the email is a Cimento simulation
 * @returns isSimulation flag and tracking id if it is a simulation
 * For testing purposes it currently returns true and a fixed tracking id
 */  
export async function isCimentoSimulation() {
  try {
    const rawHeaders = await getHeaders();   // waits here until headers arrive
    if (rawHeaders === "") {
      return { isSimulation: false, trackingId: null };
    }
    const parsed = rawHeaders
      .split(/\r?\n/)
      .map(line => {
        const idx = line.indexOf(":");
        if (idx === -1) return null;
        return {
          name: line.substring(0, idx).trim(),
          value: line.substring(idx + 1).trim()
        };
      })
      .filter(Boolean);

    for (const header of parsed) {
      if (header.name.toLowerCase() === "x-cimento-tracking") {
        return { isSimulation: true, trackingId: header.value };
      }
    }

    return {
      isSimulation: false,
      trackingId: null
    };

  } catch (error) {
    console.error("Error reading headers:", error);
    return { isSimulation: false, trackingId: null };
  }
}

/**
 * Sends simulation tracking information to the Cimento API
 * 
 * @param {string} trackingId - The tracking ID for the simulation
 * @returns {Promise<Response>} Fetch response
 * 
 * If the fetch fails with a NetworkError when attempting to fetch resource,
 * return a successful response with a status of 200 and a message of "consider as a successful response"
 */
export async function sendSimulationTracking(trackingId, apiKey) {
  logToConsole("Sending simulation tracking");

  await fetchWithTimeout(
    'https://' + CIMENTO_APP + '/api/phishing/track',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        trackingId: trackingId,
        eventType: 'reported_email'
      })
    },
    TIMEOUT_TRACKING
  )
  .then(response => {
    logToConsole("sendSimulationTracking was successful");

    console.log('Response:', response.status, response.statusText);
    return response;
  })
  .catch(error => {
    console.error("sendSimulationTracking was not successful");

    if (error.message.includes("NetworkError when attempting to fetch resource")) {

      return {
          message: "Consider as a successful response",
          status: 200
      }
    } 
    throw error;
  });
}

/**
 * Send an email that a user has reported as a phishing email
 * but it is not part of a Cimento phishing campaign
 * 
 * @param {string} messageId: messageId,
 * @param {string} destination: destination,
 * @param {string} userId: userId,
 * @param {string} tenant: tenantName
 * @param {string} apiKey: apiKey
 * @returns {Promise<Response>} Fetch response
 * 
 * If the fetch fails with a NetworkError when attempting to fetch resource,
 * return a successful response with a status of 200 and a message of "consider as a successful response"
 */
export async function reportPhishingEmail(messageId, destination, userId, tenantName, apiKey) {
  logToConsole("Sending phishing email report for messageId: " + messageId)

  const convertedMessageId = convertItemIdToGraphId(messageId)

  logToConsole("Sending phishing email report for converted messageId: " + convertedMessageId)

  const requestBody = {
    messageId: convertedMessageId,
    destination: destination,
    userId: userId,
    tenant: tenantName
  };

  const headers = {
    'Authorization': `Bearer ${apiKey}`,
    'Content-Type': 'application/json'
  };

  logToConsole("Reporting phishing email using URL: " + REPORT_URL);   

  await fetchWithTimeout(
    REPORT_URL,
    {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(requestBody)
    },
    TIMEOUT_REPORT
  )
  .then(response => {
    logToConsole("reportPhishingEmail was successful");

    console.log('Response:', response.status, response.statusText);
    return response;
  })
  .catch(error => {
    console.error("reportPhishingEmail was not successful");

    if (error.message.includes("NetworkError when attempting to fetch resource")) {
      console.error("reportPhishingEmail will be considered success anyway");
      return {
          message: "Consider as a successful response",
          status: 200
      }
    } 
    throw error;
  });
}

/**
* This uses the backend service for deleting an email using handleDeleteMessage
* messageId Need to convert to graph id before calling this function
* because of timiing issues when clicking OK button that performs the delete using the backend service
* 
* @param {string} messageId - The Office.js itemId of the email to delete
* @returns {Promise<{success: boolean, messageId: string, error?: string}>}
*/
export async function deleteEmailUsingCimentoAPI(convertedMessageId, userEmail, apiKey) {

  const url = DELETE_URL + convertedMessageId + '?outlookUser=' + userEmail;

  logToConsole("Deleting email using URL: " + url);
  
  await fetchWithTimeout(
    url,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`,
      },
    },
    TIMEOUT_DELETE
  )
  .then(response => {
    logToConsole("Moving mailbox item to deleted folder was successful");

    console.log('Response:', response.status, response.statusText);
    return response;
  })
  .catch(error => {
    console.error("Moving mailbox item to deleted folder was not successful");

    if (error.message.includes("NetworkError when attempting to process resource")) {

      return {
          message: "Consider as a successful response",
          status: 200
      }
    } 
    throw error;
  });
}

/**
 * Wrapper for fetch with timeout and abort controller to protect against hung API calls
 * 
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options (headers, method, body, etc.)
 * @param {number} timeoutMs - Timeout in milliseconds
 * @returns {Promise<Response>} Fetch response
 */
export async function fetchWithTimeout(url, options = {}, timeoutMs) {

  const controller = new AbortController();

  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {

    logToConsole("fetchWithTimeout before fetch url is " + url + " and options are " + JSON.stringify(options));

    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });

    logToConsole('Success in fetch with timeout');
    logToConsole('Response status:', response.status, response.statusText);
    clearTimeout(timeoutId);
    logToConsole('Cleared timeout in fetch with timeout');
    return response;

  } catch (error) {
    clearTimeout(timeoutId);
    console.error('fetchWithTimeout caught error:', error.name, error.message, error);
    if (error.name === 'AbortError') {
      throw new Error(`Request timeout after ${timeoutMs}ms`);
    }
    if (error.response && error.response.status === 404) {
      console.error('Item not found. Displaying a 404 message to the user.');
      // Update the UI to show a "Not Found" message
    } else {
      console.error('An unexpected error occurred:', error.message, error);
    }
    throw error;
  }
}

/*
* Used while testing to fetch a hardcoded apiKey, phishingInbox, and tenantName
*/
export async function getConfig() {
  const apiKey = configValues.apiKey;
  const phishingInbox = configValues.phishingInbox;
  const tenantName = configValues.tenantName;

  return {
    apiKey: apiKey,
    phishingInbox:  phishingInbox,
    tenantName: tenantName
  };
}

/*
  Gets a test Phishing Inbox address for the add-in
*/
export async function getPhishingInbox() {
  await sleep(2000); // Pause for 2 seconds
  return "infosec@aryaka.com";
}

/**
 * Format a date/time value for display
 * There are two options for the date/time format:
 * November 16, 2025, 1:16:27�PM EST - used by gmail side panel
 * Sun, 16 Nov 2025 12:16:27 -0600- used by gmail dialog
 * @param {Date|string} dateTime - The date/time to format
 * @returns {string} Formatted date string (e.g., "January 15, 2024 at 2:30:27 PM")
 */
export function formatDateTime(dateTime) {
  if (!dateTime) return '';
  
  const date = dateTime instanceof Date ? dateTime : new Date(dateTime);
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return String(dateTime); // Return original if invalid
  }
  
  // Format: "January 15, 2024, 2:30:27 PM EST"
  const options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
    hour12: true
  };
  
  return date.toLocaleString('en-US', options).replace(' at ', ', ');
}

/**
 * Format a date/time value in RFC 2822 format
 * Format: "Sun, 16 Nov 2025 12:16:27 -0600"
 * @param {Date|string} dateTime - The date/time to format
 * @returns {string} Formatted date string in RFC 2822 format
 */
export function formatDateTimeRFC2822(dateTime) {
  if (!dateTime) return '';
  
  const date = dateTime instanceof Date ? dateTime : new Date(dateTime);
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return String(dateTime); // Return original if invalid
  }
  
  // Day of week abbreviations
  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  // Month abbreviations
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  const dayOfWeek = daysOfWeek[date.getDay()];
  const day = String(date.getDate()).padStart(2, '0');
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  
  // Format time in 24-hour format (HH:MM:SS)
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const time = `${hours}:${minutes}:${seconds}`;
  
  // Get timezone offset in minutes and convert to �HHMM format
  const offsetMinutes = date.getTimezoneOffset();
  const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
  const offsetMins = Math.abs(offsetMinutes) % 60;
  const offsetSign = offsetMinutes <= 0 ? '+' : '-';
  const timezoneOffset = `${offsetSign}${String(offsetHours).padStart(2, '0')}${String(offsetMins).padStart(2, '0')}`;
  
  return `${dayOfWeek}, ${day} ${month} ${year} ${time} ${timezoneOffset}`;
}

/**
 * Escape HTML to prevent XSS
 */
export function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/*
  Pauses for a given number of milliseconds
*/
async function sleep(ms) {
  if (typeof setTimeout === 'undefined') {
    return Promise.resolve();
  }
  return new Promise(resolve => {
    try {
      setTimeout(() => resolve(), ms);
    } catch (error) {
      resolve(); // Resolve anyway to prevent hanging
    }
  });
}

/**
 * Converts Office.js itemId to Microsoft Graph message ID format
 * Office.js itemId is base64 encoded and needs to be decoded and converted
 * @param {string} itemId - The Office.js itemId
 * @returns {string} The Microsoft Graph message ID
 */
export function convertItemIdToGraphId(itemId) {

  try {
    // Office.js itemId format: base64 encoded EWS ID
    // We need to use Office.context.mailbox.convertToRestId to convert it
    if (Office.context && Office.context.mailbox && Office.context.mailbox.convertToRestId) {
      const restId = Office.context.mailbox.convertToRestId(
        itemId,
        Office.MailboxEnums.RestVersion.v2_0
      );
      return restId;
    } else {
      // Fallback: Use the itemId directly if it's already in REST format
      console.warn("convertToRestId not available, using itemId directly");
      return itemId;
    }
  } catch (error) {
    console.error("Error converting itemId to Graph ID:", error);
    return itemId;
  }
}

/**
 * Helper function to get the headers from the email
 * @returns {Promise<string[]>} - The headers
 */
function getHeaders() {
  return new Promise((resolve, reject) => {
    Office.context.mailbox.item.getAllInternetHeadersAsync((asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
        resolve(asyncResult.value);
      } else {
        reject(asyncResult.error);
      }
    });
  });
}

export function logToConsole(message) {
  if (debug === true) console.log(message);
}

/*
* Tests of API calls, authentication, and using delegated permissions start here
* These are for reference only
*/

/*
  Gets the customer variables for the add-in.
  This will be replaced using the backend service
*/
export async function getCustomerVariables() {
  const clientId = 'bd4179a1-baef-43ea-920d-fa6ad3739274';
  const customAttributeSetName = "outlookaddins";
  const customAttributeName = "Customer Variables";

  try {
   
    const scopes = ["CustomSecAttributeAssignment.Read.All", "Application.Read.All"];
    
    // Step 1: Get access token silently (no popup if user is already logged in)
    let accessToken = null;

    const access = await getPublicClientApplicationToken(scopes) ;
    
    if (access.success) {
      accessToken = access.accessToken;
      
      // Step 2: Get the service principal id
      const servicePrincipalId = await getServicePrincipalId(accessToken, clientId);

      if (servicePrincipalId.success) {

        // Step 3: get the custom attributes
        const attributes = await getCustomSecurityAttributes(accessToken, servicePrincipalId.id);
        if (attributes.success) {
          const customSecurityAttributes = attributes.attributes;
          
          // Log the structure to help debug
          logToConsole("Custom Security Attributes structure:", JSON.stringify(customSecurityAttributes, null, 2));
          
          // Handle different possible structures of customSecurityAttributes
          // Structure can be: { "attributeSetName": { "outlookAddins": "jsonString" } }
          // OR: { "outlookAddins": "jsonString" }
          let outlookAddinsValue = null;
          
          if (customSecurityAttributes) {
            // First, check if outlookAddins is directly on the root
            if (customSecurityAttributes.outlookaddins) {
              outlookAddinsValue = customSecurityAttributes.outlookaddins;
            } else {
              // Otherwise, look for it in nested attribute sets
              // Get the first attribute set name (custom security attributes can have multiple sets)
              const attributeSetNames = Object.keys(customSecurityAttributes);
              for (const attributeSetName of attributeSetNames) {
                if (customSecurityAttributes[attributeSetName]?.outlookaddins) {
                  outlookAddinsValue = customSecurityAttributes[attributeSetName].outlookaddins;
                  break;
                }
              }
            }
          }
          
          if (!outlookAddinsValue) {
            console.error("Could not find 'outlookAddins' in custom security attributes");
            return { success: false, error: "outlookAddins attribute not found in custom security attributes" };
          }
          
          // Parse the JSON string
          let jsObject;
          try {
            jsObject = typeof outlookAddinsValue === 'string' 
              ? JSON.parse(outlookAddinsValue) 
              : outlookAddinsValue;
          } catch (parseError) {
            console.error("Error parsing outlookAddins JSON:", parseError);
            return { success: false, error: `Failed to parse outlookAddins: ${parseError.message}` };
          }
          
          // Extract the values
          const apiKey = jsObject.apiKey;
          const phishingInbox = jsObject.phishingInbox;
          const tenantName = jsObject.tenantName;
          
          logToConsole("API Key:", apiKey);
          logToConsole("Phishing Inbox:", phishingInbox);
          logToConsole("Tenant Name:", tenantName);
          
          // Return the extracted values
          return {
            success: true,
            apiKey: apiKey,
            phishingInbox: phishingInbox,
            tenantName: tenantName
          };
        } else {
          console.error("Error getting custom security attributes:", attributes.error);
          return { success: false, error: attributes.error };
        }
      } else {
        console.error("Error getting service principal id:", servicePrincipalId.error);
        return { success: false, error: servicePrincipalId.error };
      }
    } else {

      console.error("Error getting access token:", access.error);
      return { success: false, error: access.error };
    }
  } catch (error) {
    console.error("Error in getCustomerVariables:", error);
    return { success: false, error: error.message || 'Unknown error' };
  }
}

/*
  Gets the custom security attributes for the service principal.
  This is used to get the customer variables for the add-in.
  This will be replaced using the backend service
*/
async function getCustomSecurityAttributes(accessToken, servicePrincipalId) {

  try {
    logToConsole(`About to get the custom security attributes for the principal with id ${servicePrincipalId}`);
    
    const graphEndpoint = `https://graph.microsoft.com/v1.0/servicePrincipals/${servicePrincipalId}?$select=customSecurityAttributes`;
    
    const response = await fetch(graphEndpoint, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.ok || response.status === 204) {
      const data = await response.json();
      logToConsole("Data:", data);
      logToConsole("Data: data.customSecurityAttributes", data.customSecurityAttributes);
      logToConsole(`Custom Security Attributes returned successfully`);
      return { success: true, attributes: data.customSecurityAttributes };
    } else {
      const errorText = await response.text();
      console.error(`Failed to get Custom Security Attributes: HTTP ${response.status} - ${errorText}`);
      return { success: false, attributes: null, error: `HTTP ${response.status}: ${errorText}` };
    }
  } catch (fetchError) {
    console.error('Error fetching Custom Security Attributes via Graph API:', fetchError);
    return { success: false, attributes: null, error: fetchError.message || 'Unknown error' };
  }

}

/*
  Gets the Service Principal ID that is used to get the custom security attributes.
  This will be replaced using the backend service
*/
async function getServicePrincipalId(accessToken, appId) {
  try {
    logToConsole(`About to get the principal id for the appId ${appId}`);
    
    const graphEndpoint = `https://graph.microsoft.com/v1.0/servicePrincipals(appId='${appId}')`;
    
    logToConsole(`Making GET request to: ${graphEndpoint}`);
    
    const response = await fetch(graphEndpoint, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      const data = await response.json();
      logToConsole("Data:", data);
      logToConsole("Data: data.id", data.id);

      return { success: true, id: data.id };
    } else {
      const errorText = await response.text();
      console.error(`Failed to return principal for App ID ${appId}: HTTP ${response.status} - ${errorText}`);
      return { success: false, id: null, error: `HTTP ${response.status}: ${errorText}` };
    }
  } catch (fetchError) {
    console.error('Error fetching principal via Graph API:', fetchError);
    return { success: false, id: null, error: fetchError.message || 'Unknown error' };
  }
}

/**
 * Decode a JWT token to access its JSON payload
 * JWT tokens have three parts: header.payload.signature
 * The payload is base64url encoded JSON
 * 
 * @param {string} token - The JWT token string
 * @returns {Object} The decoded JSON payload
 */
function decodeJwtToken(token) {
  try {
    // JWT tokens have three parts separated by dots
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT token format');
    }

    // The payload is the second part (index 1)
    const payload = parts[1];

    // Base64url decode (replace - with +, _ with /, and add padding if needed)
    let base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    
    // Add padding if needed
    while (base64.length % 4) {
      base64 += '=';
    }

    // Decode from base64 and parse as JSON
    const decoded = atob(base64);
    const jsonPayload = JSON.parse(decoded);

    return jsonPayload;
  } catch (error) {
    throw new Error(`Failed to decode JWT token: ${error.message}`);
  }
}

/*
  Get an access token using MSAL.js with silent acquisition (no popup if user already logged in)
  This will be replaced using the backend service
*/
export async function getPublicClientApplicationToken(scopes) {
  try {
    logToConsole("Starting process to get MSAL token silently");
    
    const tenantId = configValues.tenantId;
    const clientId = configValues.clientId;

    const domain = location.hostname + (location.port ? ':' + location.port : '');

    // If running against an actual tenant but running locally, use the azure name
    // Need to have a landing page of some sort

    //const redirectUri = `https://${domain}/redirect`;
    const redirectUri = `https://aaddin.cimento.ai/`;
    
    // using common requires the app-in to be deployed as multi-tenant application
    // Initialize MSAL with PublicClientApplication
    const pca = new PublicClientApplication({
      auth: {
        clientId: clientId,
        authority: `https://login.microsoftonline.com/${tenantId}`,
        //authority: `https://login.microsoftonline.com/common`,
        redirectUri: redirectUri,
      },
      cache: {
        cacheLocation: "sessionStorage", // Use sessionStorage for better cache behavior
        storeAuthStateInCookie: false,
      }
    });

    await initializeMsal(pca);

    // Step 1: Get access token silently (no popup if user is already logged in)
    let accessToken;
    let authResult = null;
    
    try {
      // First, try to get all accounts silently from MSAL cache
      const accounts = await pca.getAllAccounts();
      logToConsole(`Found ${accounts.length} cached account(s) in MSAL`);

      if (accounts.length > 0) {
        // Use the first account (should match the logged-in Outlook user)
        account = accounts[0];
        logToConsole(`Using cached account: ${account.username}`);

        // Try to get token silently from cache - this should work if user is already logged in
        try {
          authResult = await pca.acquireTokenSilent({
            scopes: scopes,
            account: account,
            forceRefresh: false  // Use cached token if available
          });
          logToConsole("✅ Successfully acquired token silently from cache (no popup needed)");
        } catch (silentError) {
          console.warn("Silent token acquisition failed:", silentError.errorCode || silentError.message);
          
          // If it's just an interaction_required error, we need to show popup
          // But first, let's try with a different approach - using ssoSilent
          if (silentError.errorCode === "interaction_required" || 
              silentError.errorCode === "consent_required" ||
              silentError.errorCode === "login_required") {
            logToConsole("Interaction required - will need to show popup");
            authResult = await pca.acquireTokenPopup({
              scopes: scopes,
              account: account
            });
            logToConsole("✅ Acquired token via popup (first time consent)");
          } else {
            throw silentError;
          }
        }
      } else {
        // No cached accounts - try ssoSilent first (browser SSO), then popup if needed
        logToConsole("No cached accounts found, trying SSO silent...");
        try {
          logToConsole("Pub 1");
          // Try SSO silent first - uses browser cookies if user is logged in
          authResult = await pca.ssoSilent({
            scopes: scopes,
            loginHint: Office.context.mailbox.userProfile.emailAddress // Use Outlook email
          });
          logToConsole("Pub 2");
          account = authResult.account;
          logToConsole("✅ Successfully acquired token via SSO silent (no popup)");
        } catch (ssoError) {
          console.warn("SSO silent failed, will try popup:", ssoError.errorCode || ssoError.message);
          // Last resort - show popup
          authResult = await pca.acquireTokenPopup({
            scopes: scopes
          });
          logToConsole("Pub 3");
          account = authResult.account;
          logToConsole("✅ Acquired token via popup");
        }
      }

      if (!authResult || !authResult.accessToken) {
        //return { success: false, messageId: messageId, error: "Failed to get access token" };
        return { success: false, error: "Failed to get access token" };
      }

      accessToken = authResult.accessToken;
      logToConsole("Successfully obtained MSAL access token for Graph API");

      // Decode the JWT access token to access the JSON payload
      try {
        const decodedToken = decodeJwtToken(accessToken);
        logToConsole("Decoded access token payload:", decodedToken);
        // You can now access properties like:
        // decodedToken.aud (audience)
        // decodedToken.exp (expiration timestamp)
        // decodedToken.iat (issued at timestamp)
        // decodedToken.oid (object ID)
        // decodedToken.upn (user principal name)
        // decodedToken.name, decodedToken.email, etc.
      } catch (decodeError) {
        console.warn("Failed to decode access token:", decodeError);
      }
      
      return { success: true, accessToken: accessToken };

    } catch (tokenError) {
      console.error("Failed to get MSAL token:", tokenError);
      return { success: false, error: "Failed to get access token: " + tokenError.message };
    }
  } catch (error) {
    console.error("Error in getPublicClientApplicationToken:", error);
    return { success: false, error: error.message || 'Unknown error' };
  }
}

/**
* Gets an access token using MSAL.js with silent acquisition (no popup if user already logged in)
* and deletes the current Outlook email using Microsoft Graph API
* 
* IMPORTANT: Office.js SSO tokens cannot be used directly with Microsoft Graph API.
* Office.js SSO tokens are "bootstrap tokens" scoped to the add-in resource (not Graph).
* This function uses MSAL.js to get a Graph token directly, trying silent methods first:
* 1. Checks MSAL cache for existing tokens
* 2. Uses acquireTokenSilent() if account exists in cache
* 3. Uses ssoSilent() to leverage browser SSO cookies
* 4. Only shows popup as last resort
* 
* Alternative: Exchange Office.js SSO bootstrap token via backend service using 
* "on-behalf-of" flow (requires backend service).
* 
* @param {string} messageId - The Office.js unconverted itemId of the email to delete
* @returns {Promise<{success: boolean, messageId: string, error?: string}>}
*/
async function deleteCurrentEmail(messageId) {
  try {
    const scopes = ["Mail.ReadWrite"];
    
    // Step 1: Get access token silently (no popup if user is already logged in)
    let accessToken = null;

    const access = await getPublicClientApplicationToken(scopes) ;
    if (access.success) {
      accessToken = access.accessToken;
      
      // Step 2: Convert Office.js itemId to Microsoft Graph message ID format
      const convertedMessageId = convertItemIdToGraphId(messageId);
      logToConsole(`Unconverted message ID: ${messageId}`);
      logToConsole(`Converted message ID: ${convertedMessageId}`);

      // Step 3: Delete the email using Microsoft Graph API with the MSAL token
      try {
        logToConsole(`About to delete email with Graph API, message ID: ${messageId}, converted ID: ${convertedMessageId}`);
        
        const graphEndpoint = `https://graph.microsoft.com/v1.0/me/messages/${convertedMessageId}`;
        
        const response = await fetch(graphEndpoint, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok || response.status === 204) {
          console.log(`Email with ID ${messageId} (converted ID: ${convertedMessageId}) deleted successfully`);
          return { success: true, messageId: messageId };
        } else {
          const errorText = await response.text();
          console.error(`Failed to delete email: HTTP ${response.status} - ${errorText}`);
          return { success: false, messageId: messageId, error: `HTTP ${response.status}: ${errorText}` };
        }
      } catch (deleteError) {
        console.error('Error deleting email via Graph API:', deleteError);
        return { success: false, messageId: messageId, error: deleteError.message || 'Unknown error' };
      }

    }

  } catch (error) {
    console.error("Error in deleteCurrentEmail:", error);
    return { success: false, messageId: messageId, error: error.message || 'Unknown error' };
  }
}

// Initialize MSAL before calling any other MSAL API
async function initializeMsal(pca) {
  try {
      await pca.initialize();
      logToConsole("MSAL initialized successfully.");
      // Now you can call other MSAL APIs like acquireTokenSilent, loginPopup, etc.
  } catch (error) {
      console.error("MSAL initialization failed:", error);
  }
}

/**
* Gets an access token using MSAL.js with silent acquisition (no popup if user already logged in)
* and deletes the current Outlook email using Microsoft Graph API
* 
* IMPORTANT: Office.js SSO tokens cannot be used directly with Microsoft Graph API.
* Office.js SSO tokens are "bootstrap tokens" scoped to the add-in resource (not Graph).
* This function uses MSAL.js to get a Graph token directly, trying silent methods first:
* 1. Checks MSAL cache for existing tokens
* 2. Uses acquireTokenSilent() if account exists in cache
* 3. Uses ssoSilent() to leverage browser SSO cookies
* 4. Only shows popup as last resort
* 
* Alternative: Exchange Office.js SSO bootstrap token via backend service using 
* "on-behalf-of" flow (requires backend service).
*
* This tests the backend service for deleting an email using DeleteEmailHandler
* 
* @param {string} messageId - The Office.js itemId of the email to delete
* @returns {Promise<{success: boolean, messageId: string, error?: string}>}
*/
async function deleteCurrentEmailUsingAPI(messageId, userEmail, apiKey) {

  // First convert email id converted format
  const convertedMessageId = convertItemIdToGraphId(messageId);
  
  await fetchWithTimeout(
    'https://localhost:8080/outlook/messages/movetodeleted',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        messageId: convertedMessageId,
        userEmail: userEmail
      })
    },
    TIMEOUT_DELETE
  )
  .then(response => {
    logToConsole("Moving mailbox item to deleted folder was successful");

    console.log('Response:', response.status, response.statusText);
    return response;
  })
  .catch(error => {
    console.error("Moving mailbox item to deleted folder was not successful");

    if (error.message.includes("NetworkError when attempting to process resource")) {

      return {
          message: "Consider as a successful response",
          status: 200
      }
    } 
    throw error;
  });
}
